import React, { useEffect, useRef, useState } from 'react';
import { Pagination, Stack, Typography } from '@mui/material';
import {
  fetchEquiTrack,
  fetchEquiTrackPrice
} from '../../utils';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { api as axios } from '../../api';
import { usePoll } from '../../hooks/usePoll';
import * as actionTypes from '../../store/actionTypes';
import TokenTable from '../../components/Tables/TokenTable/token.table';
import EthWallet from '../../components/nav/web3wallet';
import CommonMenu from '../../components/commonMenu/commonMenu';
import MarketStats from '../../components/MarketStats/MarketStats';
const PER_PAGE = 25;

const EquiTrackTable = () => {
  const dispatch = useDispatch();
  const loading = false;
  const {
    PecuPrice,
    equiTrack,
    equiTrackTotalPages,
    equiTrackTotalItems,
    equiTrackPrice,
    UserInfo,
    isAdmin
  } = useSelector(
      (state) => ({
        PecuPrice: state.PecuPrice,
        equiTrack: state.equiTrack,
        equiTrackTotalPages: state.equiTrackTotalPages,
        equiTrackTotalItems: state.equiTrackTotalItems,
        equiTrackPrice: state.equiTrackPrice,
        UserInfo: state.UserInfo,
        isAdmin: state.isAdmin
      }),
      shallowEqual
    );

  const [open, setOpen] = useState(false);
  const [selectedToken, setSelectedToken] = useState({});
  const [tvl, setTvl] = useState();
  const [priceList, setPriceList] = useState([]);
  const [blinkUpList, setBlinkUpList] = useState([]);
  const [blinkDownList, setBlinkDownList] = useState([]);
  const [equiTrackPriceTick, setEquiTrackPrice] = useState(equiTrackPrice);
  const [trendHourly, setTrendHourly] = useState({});
  const [coinVolume, setCoinVolume] = useState(114826.00453596);
  const [trend, setTrend] = useState({});
  const [priceDown, setPriceDown] = useState(false);
  const [searchToken, setSearchToken] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const queryKeyRef = useRef('');

  // Debounce the search box and jump back to page 1 when it changes.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch((searchToken || '').trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchToken]);

  // If the list shrinks (tokens deactivated, narrower search), don't strand
  // the user on a page that no longer exists.
  useEffect(() => {
    if (equiTrackTotalPages && page > equiTrackTotalPages) {
      setPage(equiTrackTotalPages);
    }
  }, [equiTrackTotalPages, page]);
  const filterToken = (searchToken) => {
    if (searchToken && searchToken !== '') {
      return equiTrack?.filter(
        (e) =>
          e.symbol.toLowerCase().includes(searchToken.toLowerCase()) ||
          e.name.toLowerCase().includes(searchToken.toLowerCase())
      );
    } else {
      return equiTrack;
    }
  };

  const get_change_index_coin_hourly = () => {
    axios.get(`/wallet/get_change_index_coin_hourly`).then((res) => {
      setTrendHourly(res.data);
    });
  };
  const calculate_daily_volume = () => {
    axios.get(`/wallet/calculate_daily_volume`).then((res) => {
      setCoinVolume(res.data.total_volume);
    });
  };
  const get_tvl = () => {
    axios.get(`/wallet/tvl`).then((res) => {
      setTvl(res.data.tvl);
    });
  };
  const get_change_index_coin = () => {
    axios.get(`/wallet/get_change_index_coin`).then((res) => {
      setTrend(res.data);
    });
  };
  function getFormattedTime(timestampString) {
    const timestamp = new Date(timestampString);
    timestamp.setHours(timestamp.getHours());
    return timestamp;
  }

  useEffect(() => {
    get_tvl();
  }, []);

  useEffect(() => {
    // Compare current prices with the previous prices
    if (priceList.length > 0) {
      const updatedPriceList = Object.entries(equiTrackPriceTick)
        .filter(([token, price]) => {
          const prevPrice = priceList.find((item) => item.token === token);
          return prevPrice && prevPrice.price !== price;
        })
        .map(([token, price]) => ({ token, price }));

      if (updatedPriceList.length > 0) {
        const newBlinkUpList = updatedPriceList.filter((item) => {
          const prevPrice = priceList.find(
            (prevItem) => prevItem.token === item.token
          );
          return prevPrice && item.price > prevPrice.price;
        });

        const newBlinkDownList = updatedPriceList.filter((item) => {
          const prevPrice = priceList.find(
            (prevItem) => prevItem.token === item.token
          );
          return prevPrice && item.price < prevPrice.price;
        });

        setPriceList((prevPriceList) => [
          ...prevPriceList.filter(
            (item) =>
              !updatedPriceList.some(
                (updatedItem) => updatedItem.token === item.token
              )
          ),
          ...updatedPriceList
        ]);

        setBlinkUpList(newBlinkUpList.map((item) => item.token));
        setBlinkDownList(newBlinkDownList.map((item) => item.token));

        setTimeout(() => {
          setBlinkUpList([]);
          setBlinkDownList([]);
        }, 500);
      }
    } else {
      setPriceList(
        Object.entries(equiTrackPrice).map(([token, price]) => ({
          token,
          price
        }))
      );
    }
  }, [equiTrackPriceTick, priceList]);

  usePoll(
    () => {
      calculate_daily_volume();
      get_change_index_coin();
      get_change_index_coin_hourly();
      const key = `${page}|${debouncedSearch}`;
      queryKeyRef.current = key;
      return Promise.all([
        fetchEquiTrack(
          dispatch,
          actionTypes.FETCH_EQUITRACK,
          page,
          PER_PAGE,
          debouncedSearch,
          () => queryKeyRef.current === key
        ),
        fetchEquiTrackPrice(dispatch, actionTypes.FETCH_EQUITRACK_PRICE)
      ]);
    },
    5000,
    [page, debouncedSearch]
  );

  const totalPages = Math.max(equiTrackTotalPages || 1, 1);
  const rangeStart = equiTrackTotalItems ? (page - 1) * PER_PAGE + 1 : 0;
  const rangeEnd = Math.min(page * PER_PAGE, equiTrackTotalItems || 0);

  useEffect(() => {
    setEquiTrackPrice(equiTrackPrice);
  }, [equiTrackPrice, equiTrackPriceTick]);

  return (
    <div
      className="screen"
      style={{
        zIndex: '100',
        position: 'sticky',
        width: '100%',
        marginBottom: '1rem'
      }}
    >

      <div
        style={{
          width: '95%',
          minWidth: '340px'
        }}
        className="res-token"
      >

        <MarketStats />
        <CommonMenu label={'EquiTrack'} setSearch={setSearchToken} />
        <TokenTable
          loading={loading}
          token={equiTrack}
          blinkUpList={blinkUpList}
          blinkDownList={blinkDownList}
          href={'equitrackToken'}
          PecuPrice={PecuPrice}
          swappable={true}
          getFormattedTime={getFormattedTime}
          showPecu={false}
          priceData={equiTrackPrice}
        />
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{ mt: 2, mb: 2, px: 1 }}
        >
          <Typography variant="body2" sx={{ color: '#9ca3af' }}>
            {equiTrackTotalItems
              ? `Showing ${rangeStart}-${rangeEnd} of ${equiTrackTotalItems}`
              : 'No EquiTrack tokens found'}
          </Typography>
          {totalPages > 1 && (
            <Pagination
              count={totalPages}
              page={Math.min(page, totalPages)}
              onChange={(_, value) => setPage(value)}
              shape="rounded"
              siblingCount={1}
              sx={{
                '& .MuiPaginationItem-root': { color: '#f0f0f0' },
                '& .MuiPaginationItem-root.Mui-selected': {
                  backgroundColor: '#10B981',
                  color: '#fff'
                },
                '& .MuiPaginationItem-root.Mui-selected:hover': {
                  backgroundColor: '#059669'
                }
              }}
            />
          )}
        </Stack>
      </div>
    </div>
  );
};

export default React.memo(EquiTrackTable);
